import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../services/navigation.service';

import { UserService } from '../services/user.service';
import { ClientService } from '../services/client.service';
import { SiteService } from '../services/site.service';
import { ToastService } from 'services/toast.service';

import { ROUTES } from '../constants/navigation.constants';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';

import { Client, CommonGlobalService, MultiSelect, Option, RootState, UserFilter, UserLinked } from '@umanforge/common';
import { PostClient } from 'interfaces/api_client.interfaces';
import { PostSite } from 'interfaces/api_site.interfaces';

import { DEFAULT_POST_CLIENT, getCreateClient } from '../constants/client.constants';
import { DEFAULT_POST_SITE } from '../constants/site.constants';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { Breadcrumb } from '../components/menu/breadcrumb.component';
import { Button } from '../components/inputs/button.component';
import { Input } from '../components/inputs/input.component';
import { Select } from '../components/inputs/select.component';
import { Loading } from '../components/loading.component';
import { SwitchAdminUser } from 'components/users/switch_admin_user.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { ClientsIcon } from '../components/icons/clients_icon.component';
import { SitesIcon } from '../components/icons/sites_icon.component';
import { UsersIcon } from '../components/icons/users_icon.component';
import { CountryIcon } from '../components/icons/country_icon.component';
import { CityIcon } from '../components/icons/city_icon.component';
import { ZipCodeIcon } from '../components/icons/zip_code_icon.component';
import { AddressIcon } from '../components/icons/address_icon.component';
import { SaveIcon } from '../components/icons/save_icon.component';
import { PlusIcon } from '../components/icons/plus_icon.component';

const toastService: ToastService = new ToastService( 'Create Site' );


export function CreateSiteView() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const createNewClient: Client = getCreateClient( t );

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();

  /*----------------------------- useState ------------------------------*/
  const [ postSite, setPostSite ] = useState<PostSite>( structuredClone( DEFAULT_POST_SITE ) );
  const [ postClient, setPostClient ] = useState<PostClient>( structuredClone( DEFAULT_POST_CLIENT ) );
  const [ selectedClient, setSelectedClient ] = useState<Option<Client> | undefined>();
  const [ selectedAdmins, setSelectedAdmins ] = useState<Option<UserLinked>[]>( [] );

  const [ allowedClients, setAllowedClients ] = useState<Option<Client>[]>( [] );

  const [ canPost, setCanPost ] = useState<boolean>( false );
  const [ hasChange, setHasChange ] = useState<boolean>( false );
  const [ needCreateNewClient, setNeedCreateNewClient ] = useState<boolean>( false );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( me && !CommonGlobalService.isClientAdmin( me ) ) {
      NavigationService.toSites( navigate, false );
    }
    refreshClients();
  }, [] );

  useEffect( () => {
    checkCanPostSite();
  }, [ postSite, postClient ] );

  useEffect( () => {
    checkHasChangeSite();
  }, [ postSite, postClient, selectedAdmins ] );

  useEffect( () => {
    postSite.clientId = selectedClient?.value || '';
    setSelectedAdmins( [] );
    setNeedCreateNewClient( selectedClient?.object.id === createNewClient.id );
    setPostSite( structuredClone( postSite ) );
  }, [ selectedClient ] );

  useEffect( () => {
    postSite.admins = selectedAdmins.map( admin => { return admin.object.id; } );
    setPostSite( structuredClone( postSite ) );
  }, [ selectedAdmins ] );

  /*---------------------------- Functions ------------------------------*/
  const checkCanPostSite = () => {
    const clientChange = (
      ( postSite.clientId == createNewClient.id && postClient.name != DEFAULT_POST_CLIENT.name ) ||
      ( postSite.clientId != createNewClient.id && postSite.clientId != null && postSite.clientId != '' )
    );
    const nameChange = postSite.name != null && postSite.name != '';
    setCanPost( clientChange && nameChange );
  };

  const checkHasChangeSite = () => {
    const clientChange = (
      ( postSite.clientId == createNewClient.id && postClient.name != DEFAULT_POST_CLIENT.name ) ||
      ( postSite.clientId != createNewClient.id && postSite.clientId != null && postSite.clientId != '' )
    );
    const nameChange = postSite.name != null && postSite.name != '';
    const adminChange = selectedAdmins.length > 0;
    setHasChange( clientChange || nameChange || adminChange );
  };

  const getUsers = async ( filter: UserFilter ): Promise<{ total: number, list: Option<UserLinked>[] }> => {
    const users: { total: number, list: Option<UserLinked>[] } = { total: 0, list: [] };
    if ( selectedClient && selectedClient.object.id !== createNewClient.id ) {
      if ( filter.filterName === undefined || filter.filterName === '' ) {
        delete filter.filterName;
      }
      if ( selectedClient != null ) {
        filter.filterClients = [ selectedClient.object.id ];
      }

      const result = ( await UserService.getAllowedUsers( filter as UserFilter, token, dispatch ) );
      users.total = result.total;
      users.list = CommonGlobalService.toOptions<UserLinked>(
        result.users.map<UserLinked>( user => {
          return {
            id: user.id,
            fullname: user.fullname,
            isDisable: user.isDisable,
            isAdminOfUser: user.isAdminOfUser,
            isAdmin: true,
          };
        } )
      );
    }
    return users;
  };

  const refreshClients = async (): Promise<void> => {
    setIsLoading( true );
    const response = await ClientService.getAllowedClients( null, token, dispatch );
    if ( response != null ) {
      if ( me?.isSuperAdmin ) {
        response.unshift( createNewClient );
      }
      const clients = CommonGlobalService.toOptions( response )
        .sort( client => { return client.object.id < createNewClient.id ? 1 : -1; } );
      setAllowedClients( clients );
      if ( clients.length === 1 ) {
        setSelectedClient( clients[ 0 ] );
      }
    }
    setTimeout(  () => { setIsLoading( false ); }, 75 );
  };

  const resetForm = () => {
    setSelectedAdmins( [] );
    setSelectedClient( allowedClients.length === ( me && me.isSuperAdmin ? 2 : 1 ) ? allowedClients[ 0 ] : undefined );
    setPostSite( structuredClone( DEFAULT_POST_SITE ) );
  };

  /*------------------------------ Events -------------------------------*/
  const onCreateClick = async () => {
    setIsLoading( true );
    if ( postSite.clientId === createNewClient.id ) {
      const newClientId = await ClientService.postClient( postClient, token, dispatch );
      if ( newClientId ) {
        postSite.clientId = newClientId;
      }
    }
    if ( postSite.clientId !== createNewClient.id ) {
      const newSiteId = await SiteService.postSite( postSite, token, dispatch );
      if ( newSiteId != null ) {
        toastService.showSuccess( t( 'The {0} site has been created.' ).replace( '{0}', ( postSite.name ) ) );
        resetForm();
      }
    }
    setIsLoading( false );
  };


  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ hasChange } />
      <div className='flex flex-col min-w-[350px] h-full w-full'>
        <HomeTopBar title={ t( 'Create site' ) } needConfirmationToNav={ hasChange }/>
        <Breadcrumb
          needConfirmationToNav={ hasChange }
          links={
            [
              { label: t( 'common:site' ), route: ROUTES.SITES, icon: ( <SitesIcon className='h-[14px] select-none cursor-pointer'/> ) },
              { label: t( 'Create new site' ), route: ROUTES.NEW_SITE, icon: ( <PlusIcon className='h-[12px] select-none cursor-pointer'/> ) }
            ]
          }
        />
        {
          !isLoading ?
            <div className='flex flex-col items-center justify-center'>
              <form className='relative flex flex-col gap-4 h-full w-full mb-12 items-center justify-center p-4 px-[10%]'>

                <div className='flex flex-col w-full'>
                  <Select
                    required
                    disabled={ allowedClients.length === 1 }
                    key={ allowedClients.length + ( selectedClient?.value ?? '' ) }
                    value={ selectedClient }
                    label={ t( 'Client' ) }
                    placeholder={ t( 'No associated clients' ) }
                    icon={ <ClientsIcon className='w-7 h-7' /> }
                    options={ allowedClients }
                    onChange={ option => {
                      setSelectedClient( option );
                    } }
                  />
                  <div className='w-full'>
                    <div className={`flex mx-3 border-x-2 border-b-2 rounded-b-md origin-top transition-all duration-500 ${needCreateNewClient ? 'mt-[2px] p-2 scale-100 opacity-100 max-h-96' : 'scale-y-50 opacity-0 max-h-0'}`}>
                      <Input
                        required
                        maxLength={ MAX_LENGTH_ENTITY.UF_CLIENT_NAME }
                        label={ t( 'Client name' ) }
                        placeholder={ t( 'Client' ) + '...' }
                        icon={ <ClientsIcon className='w-7 h-7' /> }
                        value={ postClient.name }
                        onChange={ newClientName => {
                          postClient.name = newClientName;
                          setPostClient( structuredClone( postClient ) );
                        } }
                      />
                    </div>
                  </div>
                </div>

                <div className='w-full'>
                  <Input
                    required
                    maxLength={ MAX_LENGTH_ENTITY.UF_SITE_NAME }
                    label={ t( 'Site name' ) }
                    placeholder={ t( 'Site' ) + '...' }
                    icon={ <SitesIcon className='w-7 h-7' /> }
                    value={ postSite.name }
                    onChange={ newSiteName => {
                      postSite.name = newSiteName;
                      setPostSite( structuredClone( postSite ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    maxLength={ MAX_LENGTH_ENTITY.UF_SITE_ADDRESS }
                    label={ t( 'Address' ) }
                    placeholder={ t( 'Address' ) + '...' }
                    icon={ <AddressIcon className='w-7 h-7' /> }
                    value={ postSite.address ?? '' }
                    onChange={ newAddress => {
                      postSite.address = newAddress;
                      setPostSite( structuredClone( postSite ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    maxLength={ MAX_LENGTH_ENTITY.UF_SITE_ADDITIONAL_ADDRESS }
                    label={ t( 'Additional address' ) }
                    placeholder={ t( 'Additional address' ) + '...' }
                    icon={ <AddressIcon className='w-7 h-7' /> }
                    value={ postSite.additionalAddress ?? '' }
                    onChange={ newAdditionalAddress => {
                      postSite.additionalAddress = newAdditionalAddress;
                      setPostSite( structuredClone( postSite ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    maxLength={ MAX_LENGTH_ENTITY.UF_SITE_ZIPCODE }
                    label={ t( 'Zip code' ) }
                    placeholder={ t( 'Zip code' ) + '...' }
                    icon={ <ZipCodeIcon className='w-7 h-7' /> }
                    value={ postSite.zipCode ?? '' }
                    onChange={ newZipCode => {
                      postSite.zipCode = newZipCode;
                      setPostSite( structuredClone( postSite ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    maxLength={ MAX_LENGTH_ENTITY.UF_SITE_CITY }
                    label={ t( 'City' ) }
                    placeholder={ t( 'City' ) + '...' }
                    icon={ <CityIcon className='w-7 h-7' /> }
                    value={ postSite.city ?? '' }
                    onChange={ newCity => {
                      postSite.city = newCity;
                      setPostSite( structuredClone( postSite ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    maxLength={ MAX_LENGTH_ENTITY.UF_SITE_COUNTRY }
                    label={ t( 'common:country' ) }
                    placeholder={ t( 'common:country' ) + '...' }
                    icon={ <CountryIcon className='w-7 h-7' /> }
                    value={ postSite.country ?? '' }
                    onChange={ newCountry => {
                      postSite.country = newCountry;
                      setPostSite( structuredClone( postSite ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <MultiSelect
                    key={ selectedClient?.value }
                    disabled={ !selectedClient || selectedClient.object.id === createNewClient.id }
                    label={ t( 'Administrators' ) }
                    placeholder={ t( 'No associated users' ) }
                    icon={ <UsersIcon className='w-7 h-7' /> }
                    clearable={ false }
                    selectedValues={ selectedAdmins }
                    getValues={ getUsers }
                    onChange={ options => {
                      setSelectedAdmins( options );
                    } }
                    extra={ selectedValue => {
                      return (
                        <SwitchAdminUser
                          objectId={ selectedValue.value }
                          isDisabled={ true }
                          defaultValue={ selectedValue.object.isAdmin }
                          onChange={ value => {
                            selectedValue.object.isAdmin = value;
                            const findedUser = selectedAdmins.find( item => { return selectedValue.object.id == item.value; } );
                            if ( findedUser ) {
                              findedUser.object.isAdmin = value;
                            }
                            setSelectedAdmins( structuredClone( selectedAdmins ) );
                          } }
                        />
                      );
                    } }
                  />
                </div>

                <div className='flex h-fit w-1/3 min-w-[160px] max-w-[250px] justify-center mt-10'>
                  <Button
                    label={ t( 'Create' ) }
                    disable={ !canPost }
                    icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
                    color='#0bb173'
                    colorHover='#088D5C'
                    onClick={ onCreateClick }
                  />
                </div>
              </form>
            </div>
            :
            <Loading/>
        }
      </div>
    </>
  );

}